/** @jsxImportSource @emotion/react */
import {FADE_IN_ANIMATION} from '../../styles/animations';
import {MIDDLE_GREY, RED_CREEK, WHITE} from '../../styles/colors';
import {SmallText} from '../../styles/styles';
import {useEffect, useState} from 'react';
import {SEARCH_BLACKLIST} from '../module';
import {Column, Row} from '../../layout/flexbox';
import * as Buttons from '../../forms/elements/buttons';
import { IconButton, InputAdornment } from '@mui/material';
import TextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import { getResource } from '../../language/resources/getResource';
import { useSelector } from 'react-redux';
import { IAppState } from '../../core/redux/IAppState';

const maxSearchLength = 40;

const ErrorPlaceholder = styled('div')`
  margin-bottom: 5px;
  min-height: 20px;
`;

const ErrorText = styled('span')`
  ${FADE_IN_ANIMATION};
  color: ${RED_CREEK};
  ${SmallText};
  margin-bottom: 10px;
  min-height: 20px;
  max-width: 800px;
`;

const CssTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: MIDDLE_GREY,
    },
    '&:hover fieldset': {
      borderColor: MIDDLE_GREY,
    },
    '&.Mui-focused fieldset': {
      borderColor: MIDDLE_GREY,
    },
  },
});


type Props = {
  onUserSearch: (query: string) => void,
  onClearUserSearch: () => void,
  errorMessage: string,
}

/**
 * SearchBox: a component that shows a search form with input and search button all stylish and stuff
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export const UserSearchBox = (props: Props) => {
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  const resources = useSelector((state: IAppState) => state.locale)

  const onUserInput = (event: any) => {
    const newValue = (event && event.target && (event.target as any).value) || '';
    setQuery(newValue);
  };

  const onUserSearchSubmit = (event: any) => { // Gets angry if we use the right event type
     // Prevent the form submission from changing the URL and causing a route re-render
    event.preventDefault();
    setError('');

     // Remove leading/trailing and extraneous middle spaces
    const trimmedQuery = query.trim().replace(/\s\s+/g, ' ');

    // If the search contains an entry in the blacklist, do not allow it.
    const blacklistedFound = SEARCH_BLACKLIST.some((token) => trimmedQuery.includes(token));
    if (blacklistedFound) {
      setError(getResource(resources, 'USER_SEARCH_BOX_BLACKLIST_WARNING'));
      return;
    }

    props.onUserSearch(trimmedQuery);
  };

  const handleClearClick = () => {
    setError('');
    setQuery('');
    props.onClearUserSearch();
  }

  useEffect(() => {
    setError(props.errorMessage);
  }, [props.errorMessage])

  return (
    <form>
      <Column>
        {
          error && error !== ''
            ? <ErrorText>{error}</ErrorText>
            : <ErrorPlaceholder/>
        }
        <Row>
          <CssTextField autoFocus={false}
                   id='user-search-input'
                   title={getResource(resources, 'USER_SEARCH_BOX_TITLE')}
                   size="small"
                   margin="none"
                   color="primary" 
                   placeholder={getResource(resources, 'USER_SEARCH_BOX_PLACEHOLDER')}
                   value={query}
                   variant="outlined"
                   onChange={onUserInput}
                   slotProps={{
                    htmlInput: {
                      maxLength: maxSearchLength,
                    },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <IconButton
                          sx={{ visibility: query ? "visible" : "hidden" }}
                          onClick={handleClearClick}>
                          <ClearIcon/>
                        </IconButton>
                      ),
                    }
                  }}
                  sx={{
                    background: WHITE,
                    width: 450,
                    m: 0,
                  }}         
            />

          <Buttons.MediumNeutral type='submit'
                              id='user-search-button'
                              onClick={onUserSearchSubmit}
                              title={getResource(resources, 'USER_SEARCH_BOX_TITLE')}>{getResource(resources, 'USER_SEARCH_BOX_SEARCH')}</Buttons.MediumNeutral>
        </Row>
      </Column>
    </form>
  );
}
